import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import SettingsPageHeader from '../components/SettingsPageHeader';
import ToastMessage from '../components/ToastMessage';
import { useToast } from '../hooks/useToast';
import { schoolSettingsApi } from '../services/schoolSettingsApi';
import { getApiErrorMessage } from '../services/settingsResponse';

const emptyForm = {
  school_name: '',
  short_name: '',
  board_name: '',
  affiliation_number: '',
  email: '',
  phone_number: '',
  website_url: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
};

const fieldClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-500';

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </span>
    {children}
  </label>
);

const SchoolProfileSettingsPage = () => {
  const {
    schoolId,
    schoolProfile,
    profileLoading,
    setSchoolProfile,
    refreshProfile,
  } = useSchool();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { toast, showToast, clearToast } = useToast();

  useEffect(() => {
    if (schoolProfile) {
      setForm(
        Object.keys(emptyForm).reduce(
          (next, key) => ({ ...next, [key]: schoolProfile[key] || '' }),
          {},
        ),
      );
    }
  }, [schoolProfile]);

  useEffect(() => {
    if (schoolId && !schoolProfile) {
      refreshProfile().catch((error) =>
        showToast(getApiErrorMessage(error, 'Failed to load school profile.'), 'error'),
      );
    }
  }, [schoolId, schoolProfile, refreshProfile, showToast]);

  const setValue = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!schoolId) {
      showToast('School ID is missing from the logged-in user.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        affiliation_number: form.affiliation_number.trim() || null,
      };
      const updated = await schoolSettingsApi.updateProfile(schoolId, payload);
      setSchoolProfile(updated);
      showToast('School profile updated successfully.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to update school profile.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (profileLoading && !schoolProfile) {
    return (
      <div className="flex min-h-[360px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading school profile...
      </div>
    );
  }

  return (
    <>
      <ToastMessage toast={toast} onClose={clearToast} />
      <SettingsPageHeader
        title="School Profile"
        description="Update the school information used throughout the ERP. School code, slug and status are controlled by the Super Admin."
      />

      <form onSubmit={handleSubmit} className="space-y-8">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Basic information</h2>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="School name" required>
              <input
                required
                value={form.school_name}
                onChange={(event) => setValue('school_name', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="Short name">
              <input
                value={form.short_name}
                onChange={(event) => setValue('short_name', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="School code">
              <input value={schoolProfile?.school_code || ''} disabled className={fieldClass} />
            </Field>
            <Field label="Slug">
              <input value={schoolProfile?.slug || ''} disabled className={fieldClass} />
            </Field>
            <Field label="Board name">
              <input
                value={form.board_name}
                onChange={(event) => setValue('board_name', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="Affiliation number">
              <input
                value={form.affiliation_number}
                onChange={(event) => setValue('affiliation_number', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="Status">
              <input value={schoolProfile?.status || ''} disabled className={fieldClass} />
            </Field>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-7">
          <h2 className="text-base font-semibold text-gray-900">Contact information</h2>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(event) => setValue('email', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="Phone number">
              <input
                value={form.phone_number}
                onChange={(event) => setValue('phone_number', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Website URL">
                <input
                  type="url"
                  placeholder="https://example.edu"
                  value={form.website_url}
                  onChange={(event) => setValue('website_url', event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-7">
          <h2 className="text-base font-semibold text-gray-900">Address</h2>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field label="Address line 1">
                <input
                  value={form.address_line1}
                  onChange={(event) => setValue('address_line1', event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Address line 2">
                <input
                  value={form.address_line2}
                  onChange={(event) => setValue('address_line2', event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
            <Field label="City">
              <input
                value={form.city}
                onChange={(event) => setValue('city', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="State">
              <input
                value={form.state}
                onChange={(event) => setValue('state', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="Country">
              <input
                value={form.country}
                onChange={(event) => setValue('country', event.target.value)}
                className={fieldClass}
              />
            </Field>
            <Field label="Pincode">
              <input
                value={form.pincode}
                onChange={(event) => setValue('pincode', event.target.value)}
                className={fieldClass}
              />
            </Field>
          </div>
        </div>

        <div className="flex justify-end border-t border-gray-200 pt-5">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save profile
          </button>
        </div>
      </form>
    </>
  );
};

export default SchoolProfileSettingsPage;
